import { useMarkConversationWakeUpFollowedUpMutation } from "@qw/graphql";
import { Column, ColumnContent, useToast } from "@qw/ui";
import { useConversation } from "@/providers/use-conversation";
import { useCreateReplyDraft } from "@/providers/use-create-reply-draft";
import { useDraftsCompose } from "@/providers/use-drafts-compose";
import { QuoteFollowUpBanner } from "./components/QuoteFollowUpBanner";
import { ConversationCollaboratorAlert } from "./conversation-collaborator-alert";
import { ConversationFooter } from "./conversation-footer";
import { ConversationHeader } from "./conversation-header";
import { ConversationMessages } from "./conversation-messages";
import { ConversationReadOnlyAlert } from "./conversation-read-only-alert";
import { InlineDraftsCompose } from "./inline-drafts-compose";
import { useLandOnNewestMessage } from "./use-land-on-newest-message";

export const Conversation = ({
  canShowInboxBesideMessage = true,
  onShowInbox,
  showBackButton = false,
}: {
  canShowInboxBesideMessage?: boolean;
  onShowInbox?: () => void;
  showBackButton?: boolean;
}) => {
  const { toast } = useToast();
  const { conversationId, loading, error, inboxConversation, isReadOnly, isCollaboratorReply } = useConversation();
  const { getDraftPlacementForConversation } = useDraftsCompose();
  const [createReplyDraft, { loading: isCreatingReplyDraft }] = useCreateReplyDraft();
  const [markFollowedUp, { loading: isMarkingFollowedUp }] = useMarkConversationWakeUpFollowedUpMutation();
  const dueWakeUp = inboxConversation?.dueWakeUp ?? null;

  useLandOnNewestMessage(conversationId, inboxConversation?.messages);

  const handleFollowUp = () => {
    if (inboxConversation?.id && dueWakeUp?.agentEmail) {
      createReplyDraft(
        { conversationId: inboxConversation.id, targetRecipientEmail: dueWakeUp.agentEmail },
        { placement: getDraftPlacementForConversation(inboxConversation.id) }
      );
    }
  };

  const handleMarkFollowedUp = () => {
    if (!dueWakeUp?.id) {
      return;
    }

    void markFollowedUp({
      variables: {
        wakeUpId: dueWakeUp.id,
      },
      refetchQueries: ["InboxConversation", "InboxConversations"],
      awaitRefetchQueries: true,
      onError: (error) => {
        toast({
          title: "Failed to mark followed up",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          variant: "destructive",
        });
      },
    });
  };

  if (!conversationId && !loading && !error) {
    return null;
  }

  return (
    <Column className="@container/conversation">
      <ConversationHeader
        canShowInboxBesideMessage={canShowInboxBesideMessage}
        onShowInbox={onShowInbox}
        showBackButton={showBackButton}
      />
      <ColumnContent>
        {isReadOnly && <ConversationReadOnlyAlert />}
        {isCollaboratorReply && <ConversationCollaboratorAlert />}
        <QuoteFollowUpBanner
          dueWakeUp={dueWakeUp}
          followUpLoading={isCreatingReplyDraft}
          markFollowedUpLoading={isMarkingFollowedUp}
          onFollowUp={handleFollowUp}
          onMarkFollowedUp={handleMarkFollowedUp}
          showActions={!isReadOnly}
        />
        {/* The composer and the footer actions sit against the newest message rather than at the
            end of the scroll, so a broker reading newest-first finds them where they are reading
            instead of past the whole history. Under oldest-first the newest message is the last
            one, which is where both already were. */}
        <ConversationMessages
          afterNewestMessage={
            !isReadOnly && (
              <>
                <InlineDraftsCompose />
                <ConversationFooter />
              </>
            )
          }
        />
      </ColumnContent>
    </Column>
  );
};
